import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import md5 from 'md5';
import CharacterDetail from './CharacterDetail';
import Background from './Background';
import ButtonSec from './ButtonSec';
import Menu from './Menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faCaretLeft } from '@fortawesome/free-solid-svg-icons';
import Seo from './Seo';  // important!

const CharacterList = () => {
  const [characters, setCharacters] = useState([]);
  const [visibleCount, setVisibleCount] = useState(24);
  const [offset, setOffset] = useState(0);
  const [backwardOffset, setBackwardOffset] = useState(0);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const publicKey = process.env.REACT_APP_MARVEL_PUBLIC_KEY;
  const privateKey = process.env.REACT_APP_MARVEL_PRIVATE_KEY;
  const [copyright, setCopyright] = useState('');

  useEffect(() => {
    const timestamp = new Date().getTime();
    const hash = md5(`${timestamp}${privateKey}${publicKey}`);

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://gateway.marvel.com/v1/public/characters?modifiedSince=2015-01-01%2C2024-01-02&orderBy=name&apikey=${publicKey}&ts=${timestamp}&hash=${hash}&limit=${visibleCount}&offset=${offset - backwardOffset}`
        );

        const filteredCharacters = response.data.data.results.filter(character => 
          !character.thumbnail.path.endsWith('image_not_available')
        );
        setCharacters(filteredCharacters);

        const copyrightText = response.data.attributionText;
        setCopyright(copyrightText);
      } catch (error) {
        console.error('Error fetching characters:', error);
      }
    };

    fetchData();
  }, [privateKey, publicKey, visibleCount, offset, backwardOffset]);


  
  const handleShowMore = () => {
    setOffset((prevOffset) => prevOffset + visibleCount);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleShowLess = () => {
    setBackwardOffset((prevBackwardOffset) => (prevBackwardOffset + visibleCount <= offset ? prevBackwardOffset + visibleCount : 0));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleCharacterClick = (character) => {
    setSelectedCharacter(character);
    setShowDetails(true);
  };

  const handleBackToList = () => {
    setShowDetails(false);
  };
  const totalPages = Math.ceil(characters.length / visibleCount);
  const currentPage = Math.ceil((offset - backwardOffset) / visibleCount) + 1;


  return (
    <Background>
      {showDetails ? (
        <CharacterDetail character={selectedCharacter} onBack={handleBackToList} />
      ) : (
        <div className="flex flex-col">
          <Menu textVariable="Marvel Characters" />
          <Seo 
            title='Character list'
            description='Characters, stories, events, comics and more.'
            type='article'
            image_link='/images/logo/mv_app_logo.png'
            keywords='mv-app, mvapp'
            url=''
          />
          <div className='flex bg-gradient-to-br from-red-500 via-red-700 to-red-800'>
            <div className='w-full text-2xl md:text-2xl lg:text-2xl text-slate-100 m-2'>
              Characters list
            </div>
          </div>

          <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2'>
          {characters.length > 0 ? (
            characters.map((character) => (
            <div className="flex flex-col md:flex-col lg:flex-col bg-gradient-to-r from-red-600 via-red-700 to-red-500 hover:shadow-xl hover:shadow-red-400/70" key={character.id}>
              <div className='w-5/5 m-4'>
                <Link to={`/characters/${character.id}`} onClick={() => window.scrollTo(0, 0)}><img className='w-full shadow-md hover:shadow-xl hover:shadow-red-400/70 transform hover:scale-95 transition-transform duration-200' src={`${character.thumbnail.path}/portrait_uncanny.${character.thumbnail.extension}`} alt={character.name} /></Link>
              </div>
              <div className="w-5/5 flex-grow m-4">
                <p className="text-xl md:text-2xl lg:text-2xl text-slate-100">{character.name}</p>
                <p className='text-l text-slate-100'>Comics: {character.comics.available} / Series: {character.series.available}</p>
                <p className='text-l text-slate-100'>Stories: {character.stories.available} / Events: {character.events.available}</p>
                <p className="text-l text-slate-50 truncate ...">{character.description}</p>
              </div>
              <div className='m-4'>
                <Link onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); }} to={`/characters/${character.id}`}><ButtonSec size="text-xl" fullWidth={true}>Character info</ButtonSec></Link>
              </div>                
            </div>
            ))
          ) : (
            <div className='py-4'>
              <div className='flex flex-col justify-center items-center'>
                <img className='w-48 items-center' src="/images/logo/loading-29.gif" alt="loading-29"/>
              </div>
            </div>
          )}
          </div>

          <div className='flex flex-row justify-center items-center py-4 border-t border-slate-100'>
            <div className='text-xl text-slate-100'><FontAwesomeIcon onClick={handleShowLess} icon={faCaretLeft}/>&emsp;Navigation - Page {currentPage}&emsp;<FontAwesomeIcon onClick={handleShowMore} icon={faCaretRight}/></div> 
          </div>
          <div className='flex flex-row justify-center border-4 border-red-100'>
            <div className='flex w-full mb-2'><ButtonSec fullWidth={true} onClick={handleShowLess}>Previous</ButtonSec></div>
            <div className='flex w-full mb-2'><ButtonSec fullWidth={true} onClick={handleShowMore}>Next</ButtonSec></div>
          </div>
          <div className='flex flex-row justify-center border-t border-slate-100 py-4'>
            <img src="/images/logo/mv_app_logo_mini.png" alt="mv-logo-app-mini"/>
          </div>
          <div className='flex flex-col justify-center items-center'>
            <div className='w-4/4 text-m text-slate-50 justify-center'><p>{copyright}</p></div>
            <div className='w-4/4 text-m text-slate-50 justify-center'><p>App PCh v.1.8</p></div>
          </div>

        </div>
      )}
    </Background>
  );
};

export default CharacterList;
